import type { ExtractedPolicy } from "@/types/extraction";

interface PolicyCoveragesCardProps {
  coverages: ExtractedPolicy["coverages"];
  discountsApplied: ExtractedPolicy["discountsApplied"];
}

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return "Not shown";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function Limit({
  label,
  perPerson,
  perAccident,
}: {
  label: string;
  perPerson: number | null;
  perAccident: number | null;
}) {
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
      <div
        className="text-xs uppercase tracking-wide font-semibold mb-1"
        style={{ color: "var(--text-secondary)", letterSpacing: 0.4 }}
      >
        {label}
      </div>
      <div style={{ color: "var(--color-navy)" }}>
        {formatCurrency(perPerson)}{" "}
        <span style={{ color: "var(--text-muted)" }}>/ person</span>,{" "}
        {formatCurrency(perAccident)}{" "}
        <span style={{ color: "var(--text-muted)" }}>/ accident</span>
      </div>
    </div>
  );
}

function SingleValue({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
      <div
        className="text-xs uppercase tracking-wide font-semibold mb-1"
        style={{ color: "var(--text-secondary)", letterSpacing: 0.4 }}
      >
        {label}
      </div>
      <div style={{ color: "var(--color-navy)" }}>{value}</div>
    </div>
  );
}

export function PolicyCoveragesCard({
  coverages,
  discountsApplied,
}: PolicyCoveragesCardProps) {
  return (
    <div>
      <div
        className="text-xs mb-3"
        style={{ color: "var(--text-secondary)" }}
      >
        These are the coverages we found on your policy. We'll use them as the
        baseline for your audit. No action needed from you here.
      </div>

      <Limit
        label="Bodily Injury Liability"
        perPerson={coverages.bodilyInjuryPerPerson.value}
        perAccident={coverages.bodilyInjuryPerAccident.value}
      />
      <SingleValue
        label="Property Damage Liability"
        value={formatCurrency(coverages.propertyDamage.value)}
      />
      <Limit
        label="Uninsured Motorist"
        perPerson={coverages.uninsuredMotoristPerPerson.value}
        perAccident={coverages.uninsuredMotoristPerAccident.value}
      />
      <Limit
        label="Underinsured Motorist"
        perPerson={coverages.underinsuredMotoristPerPerson.value}
        perAccident={coverages.underinsuredMotoristPerAccident.value}
      />
      <SingleValue
        label="Collision Deductible"
        value={formatCurrency(coverages.collisionDeductible.value)}
      />
      <SingleValue
        label="Comprehensive Deductible"
        value={formatCurrency(coverages.comprehensiveDeductible.value)}
      />
      <SingleValue
        label="Medical Payments"
        value={formatCurrency(coverages.medicalPayments.value)}
      />
      <SingleValue
        label="Personal Injury Protection"
        value={formatCurrency(coverages.personalInjuryProtection.value)}
      />
      <SingleValue
        label="Rental Reimbursement"
        value={coverages.rentalReimbursement.value ?? "Not shown"}
      />
      <SingleValue
        label="Roadside Assistance"
        value={
          coverages.roadsideAssistance.value === null
            ? "Not shown"
            : coverages.roadsideAssistance.value
              ? "Included"
              : "Not included"
        }
      />

      {discountsApplied.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div
            className="text-xs uppercase tracking-wide font-semibold mb-2"
            style={{ color: "var(--text-secondary)", letterSpacing: 0.4 }}
          >
            Discounts currently applied
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              color: "var(--color-navy)",
              fontSize: 14,
            }}
          >
            {discountsApplied.map((d, i) => {
              if (!d.name.value) return null;
              const amountText =
                d.amount.value !== null
                  ? ` (${formatCurrency(d.amount.value)})`
                  : "";
              return (
                <li key={i} style={{ marginBottom: 4 }}>
                  {d.name.value}
                  {amountText}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
